import { useCallback, useMemo, useRef } from 'react';
import type { EnvironmentKey, ConfigEntityDetail } from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useImportEntitiesIndexedDB } from '../use-import-entities-indexed-db/use-import-entities-indexed-db';
import {
  useGetAllEntitiesOfType,
  isGetAllEntitiesFailureError,
  MAX_CONCURRENCY_FOR_GET_ENTITY_BY_ID,
  MAX_RETRIES,
  RETRY_DELAY_MS,
  DEFAULT_CALL_INTERVAL,
} from '../use-get-all-entities-of-type/use-get-all-entities-of-type';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { buildOperationErrorDetail } from '../../utilities/build-operation-error-detail/build-operation-error-detail';
import {
  createBulkActionReportingStore,
  type BulkActionReportingStore,
} from '../../bulk-action-reporting/bulk-action-reporting.store';
import { withBestEffortReporting } from '../../bulk-action-reporting/with-best-effort-reporting';
import { createPaginatedFetchProgressHandlers } from '../../bulk-action-reporting/create-paginated-fetch-progress-handlers';
import {
  batchAsyncOperations,
  isRetryableError,
} from '../../utilities/batch-async-operations/batch-async-operations';

export type UseExportEntitiesOptions = {
  domainEntityType: string;
  currentAuthoringEnvironment: EnvironmentKey;
  domainEntityName: string;
  tenantId: string;
  bulkActionReportingStore?: BulkActionReportingStore;
};

/**
 * Hook that returns a callback to export all entities of a specific type.
 *
 * @param options.domainEntityType The domain entity type to export.
 * @param options.currentAuthoringEnvironment The current authoring environment.
 * @param options.domainEntityName The name of the domain entity.
 * @param options.bulkActionReportingStore Optional Zustand store for session-based
 *   reporting. When provided by a parent that subscribes to the store,
 *   state changes are automatically pushed to the UI.
 */

export const useExportEntities = ({
  domainEntityType,
  currentAuthoringEnvironment,
  domainEntityName,
  tenantId,
  bulkActionReportingStore,
}: UseExportEntitiesOptions) => {
  const reportingStore = useMemo(
    () => bulkActionReportingStore ?? createBulkActionReportingStore(),
    [bulkActionReportingStore],
  );
  const { formatMessage } = useIntl();

  const { addEntities } = useImportEntitiesIndexedDB({
    domainEntityType,
  });

  // As per requirements, we are only exporting published entities
  const getAllEntitiesOfType = useGetAllEntitiesOfType({
    environment: currentAuthoringEnvironment,
    domainEntityType,
    status: 'published',
  });

  // Used by retryFailedExports to re-fetch individual entities that failed
  const getEntityById = useConfigEntityGetByIdQuery({
    sourceEnvironment: currentAuthoringEnvironment,
    includeReferences: false,
    domainEntityType,
  });

  const { createAbortController, abort: cancelExport } = useAbortController();

  /**
   * Holds successfully exported entities in memory until the entire export
   * (including any retries) completes without failures. Only then are they
   * persisted to IndexedDB.
   */
  const pendingEntitiesRef = useRef<ConfigEntityDetail[]>([]);

  /** Writes pending entities to IndexedDB and marks the session as complete. */
  const persistEntitiesAndComplete = useCallback(async () => {
    await addEntities(pendingEntitiesRef.current, tenantId);
    pendingEntitiesRef.current = [];

    withBestEffortReporting(() => {
      reportingStore.getState().completeSession();
    });
  }, [addEntities, tenantId, reportingStore]);

  const handleExportAllEntities = useCallback(async () => {
    const abortController = createAbortController();
    pendingEntitiesRef.current = [];

    // Open the session up-front so the modal renders immediately and the
    // catch path below always has somewhere to surface failures — including
    // ones that happen before the first page is streamed.
    withBestEffortReporting(() => {
      reportingStore.getState().createSession({
        operationType: 'export',
        domainEntityType,
        sourceEnvironment: currentAuthoringEnvironment,
        metadata: {
          currentAuthoringEnvironment,
          tenantId,
        },
      });
    });

    const streamingHandlers = createPaginatedFetchProgressHandlers({
      reportingStore,
      domainEntityType,
    });

    // Collect entities as they are fetched so we have them even when
    // getAllEntitiesOfType throws a GetAllEntitiesFailureError on partial failure.
    const collectedEntities: ConfigEntityDetail[] = [];

    try {
      const { entities: exportedEntities } = await getAllEntitiesOfType(
        {
          ...streamingHandlers,
          onEntityFetched: async ({ entityId, entity }) => {
            collectedEntities.push(entity);

            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                entityName: entity.attributes?.entityMetadata?.name,
                domainEntityId:
                  entity.attributes?.entityMetadata?.domainEntityId,
                status: 'success',
              });
            });
          },
          onEntityFetchFailed: async ({ entityId, entityName, error }) => {
            const entityError = buildOperationErrorDetail(error, formatMessage);

            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                entityName,
                status: 'error',
                error: entityError,
              });
            });
          },
          onEntityRetrying: async ({ entityId }) => {
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                status: 'loading',
              });
            });
          },
        },
        abortController.signal,
      );

      // All entities fetched successfully — persist to IndexedDB
      pendingEntitiesRef.current = exportedEntities;
      await persistEntitiesAndComplete();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      // Partial failure — some entities fetched OK, others failed after retries.
      // Keep the successful ones in memory so retryFailedExports can pick up
      // where we left off without re-fetching everything.
      const isPartialFailure = isGetAllEntitiesFailureError(error);
      if (isPartialFailure) {
        pendingEntitiesRef.current = collectedEntities;
      }

      // GetAllEntitiesFailureError.message is a non-user-facing marker
      // string; for that case fall through to the localized title.
      const failureReason =
        error instanceof Error && !isPartialFailure
          ? error.message
          : formatMessage(
              { id: 'ERROR_EXPORTING_ENTITIES' },
              { domainEntityName },
            );

      withBestEffortReporting(() => {
        reportingStore.getState().failSession(failureReason);
      });
    }
  }, [
    createAbortController,
    getAllEntitiesOfType,
    persistEntitiesAndComplete,
    formatMessage,
    reportingStore,
    currentAuthoringEnvironment,
    domainEntityName,
    domainEntityType,
    tenantId,
  ]);

  /**
   * Re-fetches only the entities that failed during the initial export.
   * Merges successful results into pendingEntitiesRef and persists to
   * IndexedDB only when every entity succeeds.
   */
  const retryFailedExports = useCallback(async () => {
    // If no reporting session exists (e.g. export failed before the session
    // was created), fall back to a full re-export instead of crashing.
    if (!reportingStore.getState().session) {
      await handleExportAllEntities();
      return;
    }

    const abortController = createAbortController();

    // Reset error items back to 'loading' and get their IDs
    const { retriedEntityIds } = reportingStore
      .getState()
      .resumeSessionForRetry();

    // No item-level failures to retry (e.g. all fetches succeeded but
    // IndexedDB persistence failed). Fall back to a full re-export.
    if (retriedEntityIds.length === 0) {
      await handleExportAllEntities();
      return;
    }

    try {
      const { results: retryResults, failedCount } = await batchAsyncOperations(
        {
          items: retriedEntityIds,
          operation: async (entityId) => {
            const response = await getEntityById(entityId);
            if (!response?.data) {
              throw new Error(`Entity ${entityId} returned an empty response`);
            }
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                entityName: response.data.attributes?.entityMetadata?.name,
                domainEntityId:
                  response.data.attributes?.entityMetadata?.domainEntityId,
                status: 'success',
              });
            });
            return response;
          },
          concurrency: MAX_CONCURRENCY_FOR_GET_ENTITY_BY_ID,
          rateLimit: { intervalMs: DEFAULT_CALL_INTERVAL, intervalCap: 1 },
          continueOnError: true,
          maxRetries: MAX_RETRIES,
          retryDelayMs: RETRY_DELAY_MS,
          shouldRetry: isRetryableError,
          onItemFailed: async ({ item: entityId, error }) => {
            const entityError = buildOperationErrorDetail(error, formatMessage);
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                status: 'error',
                error: entityError,
              });
            });
          },
          onItemRetrying: async ({ item: entityId }) => {
            withBestEffortReporting(() => {
              reportingStore.getState().recordItemResult({
                sourceEntityId: entityId,
                entityType: domainEntityType,
                status: 'loading',
              });
            });
          },
          abortSignal: abortController.signal,
        },
      );

      const newlyExportedEntities = retryResults
        .map((response) => response?.data)
        .filter((data): data is ConfigEntityDetail => data != null);

      // Merge newly fetched entities into the pending set, replacing any
      // stale entries so repeated retries don't create duplicates.
      const existingById = new Map(
        pendingEntitiesRef.current.map((e) => [e.id, e]),
      );
      newlyExportedEntities.forEach((entity) => {
        existingById.set(entity.id, entity);
      });
      pendingEntitiesRef.current = Array.from(existingById.values());

      if (failedCount === 0) {
        // All retries succeeded — safe to persist everything to IndexedDB
        await persistEntitiesAndComplete();
      } else {
        withBestEffortReporting(() => {
          reportingStore
            .getState()
            .failSession(
              formatMessage(
                { id: 'ERROR_EXPORTING_ENTITIES' },
                { domainEntityName },
              ),
            );
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Discard entities fetched during this aborted retry attempt so
        // a subsequent retry doesn't persist stale partial data.
        pendingEntitiesRef.current = [];
        return;
      }

      withBestEffortReporting(() => {
        reportingStore
          .getState()
          .failSession(
            error instanceof Error
              ? error.message
              : formatMessage(
                  { id: 'ERROR_EXPORTING_ENTITIES' },
                  { domainEntityName },
                ),
          );
      });
    }
  }, [
    handleExportAllEntities,
    createAbortController,
    getEntityById,
    persistEntitiesAndComplete,
    formatMessage,
    reportingStore,
    domainEntityName,
    domainEntityType,
  ]);

  return { handleExportAllEntities, retryFailedExports, cancelExport };
};
